/* eslint-disable react/jsx-max-props-per-line */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { DialogDefaultV2 } from '../../../components/v2';
import { EXEMPLO_REGRA } from './constants';

const { criterios, nsmps } = EXEMPLO_REGRA;

/** Mesmo desenho do chip de situação da listagem: texto `.dark`, borda `.main`. */
const chipResultado = (aparece) => (
  <Chip
    size="small"
    variant="outlined"
    label={aparece ? 'Aparece' : 'Não aparece'}
    sx={aparece ? { color: 'success.dark', borderColor: 'success.main' } : { color: 'text.secondary' }}
  />
);

const celulaCriterio = (valor, combina) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    {combina
      ? <CheckIcon titleAccess="Combina" sx={{ fontSize: 16, color: 'success.main' }} />
      : <CloseIcon titleAccess="Não combina" sx={{ fontSize: 16, color: 'text.disabled' }} />}
    <Typography variant="body2" sx={{ color: combina ? 'text.primary' : 'text.secondary' }}>
      {valor}
    </Typography>
  </Stack>
);

const ExemploRegraDialog = ({ open, onClose }) => (
  <DialogDefaultV2
    open={open}
    handlerCancel={onClose}
    handlerConfirm={onClose}
    title="Exemplo de como a regra funciona"
    maxWidth="md"
    fullWidth
    readOnly
  >
    <Stack spacing={2.5}>
      <Stack spacing={1}>
        <Typography variant="body2">
          Um procedimento foi cadastrado só com estes três campos. Os demais ficaram em branco.
        </Typography>
        <Stack spacing={0.75}>
          {criterios.map((criterio) => (
            <Stack key={criterio.chave} direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                {`${criterio.campo}:`}
              </Typography>
              <Chip size="small" label={criterio.exibe} />
            </Stack>
          ))}
        </Stack>
      </Stack>

      <Stack spacing={1}>
        <Typography variant="subtitle2">Em quais NSMP ele aparece</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>NSMP</TableCell>
                {criterios.map((criterio) => (
                  <TableCell key={criterio.chave}>{criterio.coluna}</TableCell>
                ))}
                <TableCell>Resultado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {nsmps.map((nsmp) => {
                const diferentes = criterios.filter((criterio) => nsmp[criterio.chave] !== criterio.valor);
                const aparece = diferentes.length === 0;
                return (
                  <TableRow key={nsmp.nome}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{nsmp.nome}</TableCell>
                    {criterios.map((criterio) => (
                      <TableCell key={criterio.chave}>
                        {celulaCriterio(nsmp[criterio.chave], nsmp[criterio.chave] === criterio.valor)}
                      </TableCell>
                    ))}
                    <TableCell>
                      <Stack spacing={0.25} alignItems="flex-start">
                        {chipResultado(aparece)}
                        <Typography variant="caption" color="text.secondary">
                          {aparece ? 'Combina com os três campos' : `Tem ${diferentes.map((criterio) => criterio.motivo).join(' e ')}`}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Box>
        <Typography variant="subtitle2" sx={{ pb: 0.5 }}>E se...</Typography>
        <Stack component="ul" spacing={0.75} sx={{ m: 0, pl: 2.5 }}>
          <Typography component="li" variant="body2">
            <strong>A prioridade ficasse em branco:</strong> a NSMP 2 também veria o procedimento.
            Campo em branco não restringe.
          </Typography>
          <Typography component="li" variant="body2">
            <strong>Escolhesse duas prioridades, 1 e 2:</strong> valeria para NSMP de prioridade 1 ou 2.
            Dois valores no mesmo campo funcionam como OU.
          </Typography>
        </Stack>
      </Box>
    </Stack>
  </DialogDefaultV2>
);

ExemploRegraDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ExemploRegraDialog;
